import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Switch, Button, Modal, Input, Space, Typography, App, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, CloudServerOutlined, ReloadOutlined, CloudUploadOutlined,
  InfoCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../api/client';
import WorkerInstallModal, { WorkerInstallTarget } from './WorkerInstallModal';
import WorkerInfoModal, { WorkerInfoTarget } from './WorkerInfoModal';
import { WorkerTelemetry } from '../pages/WorkerAnalyticsPage';
import type { Worker } from './workers/types';
import { NameCell, LocationCell, StatusCell, LoadCell, ChecksCell, BuildCell } from './workers/cells';
import UpdateAllModal from './workers/UpdateAllModal';

const { Text } = Typography;

// The table fits ~1000px without sideways scrolling: only the narrow columns
// carry a fixed width, name and URL take what is left and ellipsis the rest.
const WorkersCard: React.FC = () => {
  const { message } = App.useApp();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newURL, setNewURL] = useState('');
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Installing the checker onto the host of a worker that is already listed.
  const [installTarget, setInstallTarget] = useState<WorkerInstallTarget | null>(null);
  const [installAction, setInstallAction] = useState<'install' | 'uninstall'>('install');
  const [infoTarget, setInfoTarget] = useState<WorkerInfoTarget | null>(null);
  const [updateAllOpen, setUpdateAllOpen] = useState(false);
  // Telemetry is history, not live state: it moves once a day's counters flush,
  // so it is fetched on its own slower schedule rather than with every poll of
  // the table.
  const [telemetry, setTelemetry] = useState<Record<string, WorkerTelemetry>>({});

  // Workers running an older checker (or one too old to say which) — what the
  // "Update all" button offers. The built-in checker is never among them.
  const needUpdate = workers.filter((w) => w.source !== 'env'
    && (w.software?.state === 'outdated' || w.software?.state === 'present')).length;

  const loadWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/workers');
      if (res.data?.success) {
        setWorkers(res.data.data || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTelemetry = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/admin/workers/telemetry?days=7');
      const byId: Record<string, WorkerTelemetry> = {};
      for (const w of data.data?.workers || []) {
        byId[w.workerId] = w;
        // Also keyed by name: a worker counted before the sampler learned its
        // id is stored under a name key, and the row still has to find it.
        byId['name:' + w.workerName] = w;
      }
      setTelemetry(byId);
    } catch {
      // The table is useful without it.
    }
  }, []);

  useEffect(() => {
    loadWorkers();
    loadTelemetry();
    const interval = setInterval(loadWorkers, 10000);
    const slow = setInterval(loadTelemetry, 60000);
    return () => { clearInterval(interval); clearInterval(slow); };
  }, [loadWorkers, loadTelemetry]);

  const handleAdd = async () => {
    if (!newName.trim() || !newURL.trim()) {
      message.error('Name and URL are required');
      return;
    }
    setAdding(true);
    try {
      const res = await apiClient.post('/admin/workers', { name: newName.trim(), url: newURL.trim() });
      if (res.data?.success) {
        message.success('Worker added');
        setAddModalOpen(false);
        setNewName('');
        setNewURL('');
        loadWorkers();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to add worker');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleEnabled = async (worker: Worker, enabled: boolean) => {
    setTogglingId(worker.id);
    try {
      const res = await apiClient.put(`/admin/workers/${worker.id}`, { enabled });
      if (res.data?.success) {
        message.success(`Worker ${enabled ? 'enabled' : 'disabled'}`);
        loadWorkers();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update worker');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (worker: Worker) => {
    try {
      const res = await apiClient.delete(`/admin/workers/${worker.id}`);
      if (res.data?.success) {
        message.success('Worker deleted');
        loadWorkers();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete worker');
    }
  };

  const telemetryOf = (w: Worker) => telemetry[w.id] || telemetry['name:' + w.name];

  const columns: ColumnsType<Worker> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: { showTitle: false },
      render: (_, record) => <NameCell worker={record} />,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: { showTitle: false },
      render: (url: string) => (
        <Text copyable={{ text: url }} ellipsis={{ tooltip: url }} style={{ fontSize: 12 }}>{url}</Text>
      ),
    },
    {
      title: 'Geo',
      key: 'location',
      width: 72,
      render: (_, record) => <LocationCell worker={record} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 112,
      render: (_, record) => <StatusCell worker={record} telemetry={telemetryOf(record)} />,
    },
    {
      title: 'Load',
      key: 'workers',
      width: 104,
      render: (_, record) => <LoadCell worker={record} />,
    },
    {
      title: 'Checks',
      key: 'telemetry',
      width: 104,
      render: (_, record) => <ChecksCell telemetry={telemetryOf(record)} />,
    },
    {
      title: <Tooltip title="Jobs waiting in this worker's queue">Queue</Tooltip>,
      key: 'queue',
      width: 64,
      render: (_, record) => (record.live?.online
        ? <Text style={{ fontSize: 12 }}>{record.live.queueDepth}</Text>
        : <Text type="secondary">-</Text>),
    },
    {
      title: 'Build',
      key: 'version',
      width: 124,
      render: (_, record) => <BuildCell worker={record} />,
    },
    {
      title: <Tooltip title="Enabled — a disabled worker keeps its entry but is sent no jobs">On</Tooltip>,
      key: 'enabled',
      width: 52,
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.enabled}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggleEnabled(record, checked)}
          disabled={record.source === 'discovered'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 112,
      render: (_, record) => {
        const openInfo = () => setInfoTarget({
          id: record.id, name: record.name, url: record.url,
          online: !!record.live?.online, runtime: record.live?.runtime,
          software: record.software, install: record.install,
          geo: { publicIp: record.publicIp, country: record.country, city: record.city, isp: record.isp },
          // What this worker sees when it asks the check targets directly.
          // Live if it is connected, otherwise the last verdict on record.
          targets: record.live?.targets ?? record.targets,
        });
        // Info is worth having for the built-in checker too; installing it
        // over SSH is not, since it lives beside the gateway in compose.
        // The buttons are icons with their words in the tooltip — the labels
        // cost 140px of table width and the icons are the same four actions.
        const info = (
          <Tooltip title="Details, and probe the host now">
            <Button type="text" size="small" icon={<InfoCircleOutlined />} onClick={openInfo} aria-label={`Details of ${record.name}`} />
          </Tooltip>
        );
        if (record.source !== 'manual') return <Space size={0}>{info}</Space>;

        // What is on the host is no longer remembered but observed: the worker
        // reports the hash of the binary it is executing. So a host set up by
        // hand, before this button existed, offers Update rather than Install
        // on top of something already running.
        const state = record.software?.state;
        const present = state === 'current' || state === 'outdated' || state === 'present';
        const label = state === 'outdated' ? 'Update'
          : state === 'current' ? 'Reinstall'
          : present ? 'Update'
          : 'Install';
        // Removal works on software this panel did not install either: same
        // files, same unit.
        const removable = present || record.install?.status === 'installed';

        return (
          <Space size={0}>
            {info}
            <Tooltip title={`${label} the checker on this host over SSH`}>
              <Button
                type="text" size="small" icon={<CloudUploadOutlined />}
                aria-label={`${label} the checker on ${record.name}`}
                onClick={() => { setInstallAction('install'); setInstallTarget({ id: record.id, name: record.name, url: record.url }); }}
              />
            </Tooltip>
            {/* An empty slot when there is nothing to remove, so Delete sits in
                the same place on every row and is not hit by mistake. */}
            {!removable && <span style={{ display: 'inline-block', width: 24 }} aria-hidden />}
            {removable && (
              <Tooltip title="Remove the checker software from the host">
                <Button
                  type="text" size="small" icon={<MinusCircleOutlined />}
                  aria-label={`Remove the checker software from ${record.name}`}
                  onClick={() => { setInstallAction('uninstall'); setInstallTarget({ id: record.id, name: record.name, url: record.url }); }}
                />
              </Tooltip>
            )}
            <Popconfirm
              title={present ? 'The checker is still installed there' : 'Delete this worker?'}
              description={present
                ? 'Only the panel entry goes away — the service keeps running on the host with nothing watching it, and the panel will no longer see it. Remove the software first.'
                : 'The entry disappears from the list. Nothing changes on the host itself.'}
              onConfirm={() => handleDelete(record)}
              okText={present ? 'Delete the entry anyway' : 'Delete'}
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              overlayStyle={{ maxWidth: 380 }}
            >
              <Tooltip title="Delete this worker entry from the panel">
                <Button type="text" danger icon={<DeleteOutlined />} size="small" aria-label={`Delete ${record.name} from the panel`} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      size="small"
      title={<Space size={6}><CloudServerOutlined />Checker Workers</Space>}
      style={{ marginBottom: 16 }}
      extra={
        <Space size={8}>
          <Button icon={<ReloadOutlined />} onClick={loadWorkers} loading={loading} size="small">
            Refresh
          </Button>
          {needUpdate > 0 && (
            <Tooltip title="Install the current checker version on every worker that runs an older one, a few at a time">
              <Button icon={<CloudUploadOutlined />} onClick={() => setUpdateAllOpen(true)} size="small">
                Update all ({needUpdate})
              </Button>
            </Tooltip>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)} size="small">
            Add Worker
          </Button>
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
        Checker instances that process health check jobs from the shared queue. Environment workers cannot be deleted but can be disabled.
      </Text>
      <Table
        columns={columns}
        dataSource={workers}
        rowKey={(r) => r.id || r.url}
        size="small"
        tableLayout="fixed"
        pagination={false}
        loading={loading}
      />

      <Modal
        title="Add Checker Worker"
        open={addModalOpen}
        onOk={handleAdd}
        onCancel={() => { setAddModalOpen(false); setNewName(''); setNewURL(''); }}
        confirmLoading={adding}
        okText="Add"
        width={480}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Text style={{ fontSize: 13 }}>Name</Text>
            <Input
              size="small"
              placeholder="e.g. Checker EU-1"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div>
            <Text style={{ fontSize: 13 }}>URL</Text>
            <Input
              size="small"
              placeholder="e.g. http://checker-2:8081"
              value={newURL}
              onChange={(e) => setNewURL(e.target.value)}
            />
          </div>
        </Space>
      </Modal>
      <WorkerInstallModal
        target={installTarget}
        action={installAction}
        onClose={() => setInstallTarget(null)}
        onFinished={loadWorkers}
      />
      <WorkerInfoModal target={infoTarget} onClose={() => setInfoTarget(null)} />
      <UpdateAllModal open={updateAllOpen} onClose={() => setUpdateAllOpen(false)} onFinished={loadWorkers} />
    </Card>
  );
};

export default WorkersCard;
